import React, { MouseEvent, ReactElement } from 'react';
import { ROUTES } from 'b2b-apps/core/routes/components/Router';
import { useRouterPush } from 'b2b-apps/core/routes/hooks/useRouterPush';
import { useUrlCreator } from 'b2b-apps/core/routes/hooks/useUrlCreator';
import { openLink } from 'b2b-apps/core/routes/utils/url';
import { AddItemsToCartContentDone, AddItemsToCartContentDoneContainerProps } from 'b2b-apps/shop/common/components';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { TranslateFunction } from 'b2b-server/core/utils/language';
import { AddItemsToCartType, CART_ITEM_ERROR_TYPE, CartItemsErrorGroupType } from 'b2b-common/core/cart/api/CartTypes';

export const getFailedItemsGroups = (data: AddItemsToCartType, t: TranslateFunction): CartItemsErrorGroupType[] => {
  const text = {
    unknownReason: t('Some items were not added to cart'),
    [CART_ITEM_ERROR_TYPE.itemSoldOut]: t('Some items were not added to cart as the requested amount is not available'),
    [CART_ITEM_ERROR_TYPE.maxOrderQuantityExceeded]: t('Some items were not added to cart as maximum order quantity is exceeded'),
    [CART_ITEM_ERROR_TYPE.legacy]: t('Some items were not added to cart as they are no longer in stock'),
    [CART_ITEM_ERROR_TYPE.missingCertificate]: t('Some items were not added to cart due to missing certificate'),
    [CART_ITEM_ERROR_TYPE.itemOutsideOfCoreAssortment]: t('Some items were not added to basket due to restrictions in assortment for this account'),
  };

  return data?.failed.map(group => ({
    ...group,
    description: text[group.description] || text.unknownReason,
  })) || [];
};

export const AddItemsToCartContentDoneContainer = ({
  data,
  onConfirm,
  onModalClose,
}: AddItemsToCartContentDoneContainerProps): ReactElement => {
  const t = useTranslate();
  const urlCreator = useUrlCreator();
  const push = useRouterPush();

  const numberOfAddedCartItems = data?.added.length || 0;
  const failedItemsGroups = getFailedItemsGroups(data, t);

  const handleProductOpen = (sku: string) => (event: MouseEvent<HTMLSpanElement>): void => {
    event.preventDefault();
    openLink(urlCreator(ROUTES.product, { sku: sku }), push);
  };

  return (
    <AddItemsToCartContentDone
      text={{
        addingItemsComplete: t('{count} items were successfully added to basket', { count: numberOfAddedCartItems }),
        addingItemsFailed: t('None of the items could be added to cart'),
        approveSuccess: t('Go to cart'),
        articleNr: t('Nr'),
        close: t('Close'),
        operationComplete: t('Operation complete'),
      }}
      onConfirm={onConfirm}
      onClose={onModalClose}
      failedItemsGroups={failedItemsGroups}
      numberOfAddedCartItems={numberOfAddedCartItems}
      onProductClick={handleProductOpen}
    />
  );
};
